import { useMemo } from 'react';
import { VegaEmbed } from 'react-vega';
import { expressionInterpreter } from 'vega-interpreter';
import { TopLevelSpec } from 'vega-lite';

import { AppointmentVideoSession } from '@/types/appointments';

interface Props {
  videoSessions: AppointmentVideoSession[];
}

const VideoSessionsChart = ({ videoSessions }: Props) => {
  const hasEstimatedLeave = useMemo(
    () => videoSessions.some((session) => session.leave_at_estimated),
    [videoSessions]
  );

  const spec: TopLevelSpec = useMemo(
    () => ({
      width: 'container',
      config: { font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
      data: { values: videoSessions },
      transform: [
        {
          calculate: "datum.leave_at_estimated ? 'Estimated (no leave detected)' : 'Recorded'",
          as: 'leave_source'
        }
      ],
      // Shared positional + tooltip encoding for both layers.
      encoding: {
        x: { field: 'join_at', type: 'temporal', title: 'Time' },
        x2: { field: 'leave_at' },
        y: { field: 'participant', type: 'nominal', title: 'Participant' },
        tooltip: [
          { field: 'participant', type: 'nominal', title: 'Participant' },
          { field: 'type', type: 'nominal', title: 'Type' },
          { field: 'join_at', type: 'temporal', title: 'Join At', format: '%H:%M' },
          { field: 'leave_at', type: 'temporal', title: 'Leave At', format: '%H:%M' },
          { field: 'leave_source', type: 'nominal', title: 'Leave Time' },
          { field: 'duration_display', type: 'nominal', title: 'Duration' }
        ]
      },
      layer: [
        {
          // Base bars: colored fill, faded when the leave time is estimated.
          mark: 'rect',
          encoding: {
            color: { field: 'type', type: 'nominal', title: 'Type' },
            fillOpacity: {
              condition: { test: 'datum.leave_at_estimated', value: 0.4 },
              value: 0.9
            }
          }
        },
        {
          // Dashed outline drawn only over estimated sessions. strokeDash must be
          // a mark property here - as a conditional encoding it is ignored on
          // rect marks, so the border would render solid.
          transform: [{ filter: 'datum.leave_at_estimated' }],
          mark: {
            type: 'rect',
            fill: null,
            stroke: '#374151',
            strokeWidth: 1.5,
            strokeDash: [4, 2]
          }
        }
      ]
    }),
    [videoSessions]
  );

  if (videoSessions.length === 0) {
    return <p className="text-sm text-gray-500">No video session data available.</p>;
  }

  return (
    <>
      <VegaEmbed
        spec={spec}
        className="w-full"
        options={{ ast: true, expr: expressionInterpreter }}
      />
      {hasEstimatedLeave && (
        <p className="mt-2 text-xs text-gray-500">
          Faded, dashed-outline bars show an <span className="font-medium">estimated</span> leave
          time — no leave event was detected, so it is estimated as 5 minutes after the scheduled
          end.
        </p>
      )}
    </>
  );
};

export default VideoSessionsChart;
